import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useMatch, useNavigate} from 'react-router-dom';
import {useForm, FormProvider} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {PREVIOUS_PAGE_STORAGE_KEY} from 'utils/constants';
import {SessionStorage} from '@chekin/core';
import {usePath} from 'hooks';
import {usePostHogTrack} from 'analytics';
import {POSTHOG_EVENTS} from 'analytics/posthog';
import {useComputedOnboardingDetails} from 'hooks/useComputedOnboardingDetails';
import {useAuth} from 'hooks/auth/useAuth';
import {PATHS} from 'routes/constants';
import {FormTypes} from '../types';
import {CREATE_ACCOUNT_STEPS, FORM_NAMES} from '../constants';
import {SocialLoginButtons} from '../components/SocialLoginButtons';
import {AccountForm} from '../components/AccountForm';
import {LoginRow} from '../components/LoginRow';
import {AuthPage} from 'pages/Auth/components/AuthPage';
import {ViewLoader} from '../components/ViewLoader';
import {Button} from '@guestapp/old-ui-kit';
import {SeparatorRow, Separator, Or} from '../styled';

export const LoginView = () => {
  const {t} = useTranslation();
  const {getFullPath} = usePath();
  const navigate = useNavigate();
  const {isLoggedIn, isInitialLoading: isLoadingAuth} = useAuth();
  const {trackEvent} = usePostHogTrack();
  const isLogin = !!useMatch(getFullPath(PATHS.auth.login));
  const [showBackToRegistration, setShowBackToRegistration] = useState(false);
  const {
    shouldAskReservationGroup,
    shouldAskNumberOfGuests,
    areGuestCreated,
    remainingAllowedGuest,
    isLoadingDetails,
  } = useComputedOnboardingDetails();

  useEffect(() => {
    const previousPage = SessionStorage.get<string>(PREVIOUS_PAGE_STORAGE_KEY);
    setShowBackToRegistration(!!previousPage?.includes('add/guest'));
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const [step, setStep] = useState(CREATE_ACCOUNT_STEPS.email);

  const methods = useForm<FormTypes>({
    defaultValues: {
      email: '',
      password: '',
      email_code: '',
    },
  });

  const {watch} = methods;
  const email = watch(FORM_NAMES.email) ?? '';
  const isEmailStep = step === CREATE_ACCOUNT_STEPS.email;

  const handleConfirm = (nextStep: CREATE_ACCOUNT_STEPS) => {
    setStep(nextStep);
  };

  const title = (() => {
    if (isLogin) {
      return t('auth.create_account.welcome_back');
    }

    switch (step) {
      case CREATE_ACCOUNT_STEPS.checkEmailCode:
        return t('auth.create_account.check_your_inbox');
      default:
        return t('auth.create_account.title');
    }
  })();

  const subtitle = (() => {
    if (isLogin) {
      return undefined;
    }
    switch (step) {
      case CREATE_ACCOUNT_STEPS.email:
        return t('auth.create_account.subtitle');
      case CREATE_ACCOUNT_STEPS.checkEmailCode:
        return (
          <Trans i18nKey="auth.create_account.enter_verification_code" values={{email}} />
        );
      default:
        return undefined;
    }
  })();

  const handleClickLogo = () => {
    setStep(CREATE_ACCOUNT_STEPS.email);
  };

  const getNextOnboardingStep = useCallback(() => {
    if (shouldAskReservationGroup) {
      return PATHS.onboarding.reservationType;
    }
    if (shouldAskNumberOfGuests) {
      return PATHS.onboarding.numberOfGuests;
    }
    return PATHS.onboarding.whoIsComing;
  }, [shouldAskReservationGroup, shouldAskNumberOfGuests]);

  const startedEventFiredRef = useRef(false);
  useEffect(() => {
    if (
      !isLoadingDetails &&
      !isLoadingAuth &&
      isLoggedIn &&
      remainingAllowedGuest > 0 &&
      !startedEventFiredRef.current
    ) {
      startedEventFiredRef.current = true;
      trackEvent(POSTHOG_EVENTS.INSTANT_CHECK_IN_STARTED, {
        auth_method: 'already_logged_in',
      });
      navigate(getFullPath(getNextOnboardingStep()), {replace: true});
    }
  }, [
    isLoggedIn,
    remainingAllowedGuest,
    isLoadingDetails,
    isLoadingAuth,
    trackEvent,
    navigate,
    getFullPath,
    getNextOnboardingStep,
  ]);

  const handleSuccessfulAuth = useCallback(
    async (authMethod: 'email' | 'google') => {
      if (areGuestCreated && remainingAllowedGuest === 0) {
        navigate(getFullPath(PATHS.auth.welcome));
        return;
      }

      trackEvent(POSTHOG_EVENTS.INSTANT_CHECK_IN_STARTED, {
        auth_method: authMethod,
        is_login: isLogin,
      });

      const nextOnboardingStep = getNextOnboardingStep();
      navigate(getFullPath(nextOnboardingStep));
    },
    [
      getFullPath,
      navigate,
      areGuestCreated,
      remainingAllowedGuest,
      getNextOnboardingStep,
      trackEvent,
      isLogin,
    ],
  );

  if (isLoadingDetails || isLoadingAuth) {
    return <ViewLoader />;
  }

  return (
    <AuthPage
      title={title}
      subtitle={subtitle}
      onClickLogo={handleClickLogo}
      isLoading={isLoading}
    >
      <FormProvider {...methods}>
        <AccountForm
          step={step}
          onConfirm={handleConfirm}
          onGoBack={() => setStep(CREATE_ACCOUNT_STEPS.email)}
          onResendEmail={() => {}}
          isLogin={isLogin}
          onSuccessAuth={() => handleSuccessfulAuth('email')}
          onLoadingChange={setIsLoading}
        />
      </FormProvider>
      {isEmailStep && (
        <>
          <LoginRow isLogin={isLogin} />
          <SeparatorRow>
            <Separator />
            <Or>{t('or')}</Or>
            <Separator />
          </SeparatorRow>
          <SocialLoginButtons
            onSuccessAuth={() => handleSuccessfulAuth('google')}
            continueWithoutLogin={!showBackToRegistration}
          />
          {showBackToRegistration && (
            <Button
              link
              onClick={() => navigate(getFullPath(PATHS.guestRegistration))}
              label={t('go_back_to_registration')}
            />
          )}
        </>
      )}
    </AuthPage>
  );
};
